<!-- eslint-disable vue/no-static-inline-styles -->
<template>
  <div
    v-for="block of activeDesktopBlocks"
    :key="block.id"
    class="mega-menu-block"
    :style="{
      gridColumnStart: `span ${block.columnsWidth}`
    }"
  >
    <div
      v-if="block.type === 'link'"
      class="mega-menu-link-block"
      :style="{
        height: linkBlockHeight && `${linkBlockHeight}px`
      }"
    >
      <a :href="block.url">{{ block.title }}</a>
    </div>

    <div
      v-if="block.type === 'links'"
      class="mega-menu-links-block"
    >
      <p class="mega-menu-block-title">
        {{ block.title }}
      </p>
      <ul>
        <li
          v-for="(link, index) of block.menu"
          :key="link.title"
          class="mega-menu-link-container"
          :class="{
            'mega-menu-link-container--highlight':
              block.highlight_last_link && index === block.menu.length - 1
          }"
        >
          <a
            :href="link.url"
            class="mega-menu-link"
          >
            {{ link.title }}
          </a>
        </li>
      </ul>
    </div>

    <div v-if="block.type === 'media-grid'">
      <p class="mega-menu-block-title">
        {{ block.title }}
      </p>
      <ul
        class="mega-menu-media-grid"
        :style="{
          '--mega-menu-media-grid--desktop-columns': block.desktopGridColumns,
        }"
        :class="{
          'mega-menu-media-grid--layout-grid': block.layout === 'grid',
          'mega-menu-media-grid--layout-list': block.layout === 'list'
        }"
      >
        <li
          v-for="card of block.cards"
          :key="card.title"
        >
          <a
            :href="card.url"
            class="mega-menu-media-grid__item"
          >
            <div
              ref="imageRefs"
            >
              <responsive-image
                block-class="mega-menu-media-grid"
                :image="card.image"
                :ratio="block.aspectRatio"
                min-max="256-512"
                :lazyload="false"
              />
            </div>
            <p v-if="block.layout === 'list'">
              {{ card.subtitle }}
            </p>
            <h3>{{ card.title }}</h3>
            <p v-if="block.layout === 'grid'">
              {{ card.subtitle }}
            </p>
          </a>
        </li>
      </ul>
    </div>
  </div>
</template>

<script>

/**
 * Vue: Predictive search (mega-menu)
 * -----------------------------------------------------------------------------
 * Section containing predictive search input field and results
 *
 */

import { mapActions } from 'vuex'
import ResponsiveImage from '../../global/images/responsive-image.vue'

export default {
  name: 'MegaMenu',

  components: {
    ResponsiveImage,
  },

  props: {
    isActive: {
      type: Boolean,
      default: false,
    },
    settings: {
      type: Object,
      default: () => ({}),
    },
    activeId: {
      type: String,
      default: null,
    },
  },

  data() {
    return {
      linkBlockHeight: null,
    }
  },

  computed: {
    activeDesktopBlocks() {
      const activePrimaryBlock = this.settings.blocks.find(block => block.title === this.activeId)

      if (!activePrimaryBlock) {
        return []
      }

      const desktopBlockIds = activePrimaryBlock.desktopBlockIds.split(',')
      const desktopBlocks = desktopBlockIds.map(blockId => {
        return this.settings.blocks.find(block => block.id === blockId)
      }).filter(Boolean)

      return desktopBlocks.map(block => {
        if (block.type === 'media-grid') {
          return {
            ...block,
            cards: block.cards.filter(card => card.title),
          }
        }

        return block
      })
    },
  },

  watch: {

    /**
     * Sets focus on input when dropdown is opened.
     */
    isActive() {
      if (this.isActive) {
        this.$nextTick().then(() => {
          this.updateFocusTrap()
        })
      }
    },
  },

  updated() {
    this.$nextTick().then(() => {
      this.measureHeight()
    })
  },

  methods: {

    /**
     * Map Vuex actions.
     */
    ...mapActions({
      setFocusTrap: 'accessibility/setFocusTrap',
    }),

    /**
     * Update focus trap using Vuex.
     */
    updateFocusTrap() {
      this.$refs.inputField.focus()
      this.setFocusTrap({ container: this.$refs.inputField })
    },

    measureHeight() {
      if (!this.$refs.imageRefs || this.$refs.imageRefs.length === 0) {
        return
      }

      const tallestElementHeight = Math.max(...this.$refs.imageRefs.map(ref => ref.clientHeight))
      if (tallestElementHeight) {
        this.linkBlockHeight = tallestElementHeight
      }
    },
  },
}
</script>

<style lang="scss">
@import "./mega-menu";
</style>
